import * as React from "react";
import { Suspense, useEffect } from "react";
import { CssVarsProvider, useColorScheme } from "@mui/joy/styles";
import CssBaseline from "@mui/joy/CssBaseline";
import customTheme from "./theme";
import "./gradients.css";
import "./scrollbar.css";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Breadcrumbs from "@mui/joy/Breadcrumbs";
import Link from "@mui/joy/Link";
import Typography from "@mui/joy/Typography";
import CircularProgress from "@mui/joy/CircularProgress";
import {
	BrowserRouter,
	Routes,
	Route,
	useNavigate,
	useLocation,
	Navigate,
} from "react-router-dom";
import { NotificationProvider } from "./contexts/NotificationContext";
import NotificationSnackbar from "./components/common/NotificationSnackbar";

import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import AddIcon from "@mui/icons-material/Add";
import ForestIcon from "@mui/icons-material/Forest";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import BarChartIcon from "@mui/icons-material/BarChart";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";

import Sidebar from "./components/Sidebar";
import { isAuthenticated, isAdmin } from "./services/api";
import { initializeCSRF } from "./services/csrf";
import Sheet from "@mui/joy/Sheet";
import Grid from "@mui/joy/Grid";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import CardActions from "@mui/joy/CardActions";
import Divider from "@mui/joy/Divider";
import Avatar from "@mui/joy/Avatar";
import Stack from "@mui/joy/Stack";

// Lazy loading de todas as páginas para melhor performance
const ClientesPage = React.lazy(() => import("./pages/ClientesPage"));
const PropriedadesPage = React.lazy(() => import("./pages/PropriedadesPage"));
const SociosPage = React.lazy(() => import("./pages/SociosPage"));
const RatificacoesPage = React.lazy(() => import("./pages/RatificacoesPage"));
const NovaRatificacaoPage = React.lazy(
	() => import("./pages/NovaRatificacaoPage"),
);
const EditarRatificacaoPage = React.lazy(
	() => import("./pages/EditarRatificacaoPage"),
);
const NovoUsuarioPage = React.lazy(() => import("./pages/NovoUsuarioPage"));
const ConfiguracoesPage = React.lazy(() => import("./pages/ConfiguracoesPage"));
const EmImplementacaoPage = React.lazy(
	() => import("./pages/EmImplementacaoPage"),
);
const HomeTemporariaPage = React.lazy(
	() => import("./pages/HomeTemporariaPage"),
);
const SuportePage = React.lazy(() => import("./pages/SuportePage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const HomePage = React.lazy(() => import("./pages/HomePage"));
const FinancialPage = React.lazy(() => import("./pages/FinancialPage"));
const FinancialDetailsPage = React.lazy(
	() => import("./pages/FinancialDetailsPage"),
);
const NewFinancialPage = React.lazy(() => import("./pages/NewFinancialPage"));
const EditFinancialPage = React.lazy(() => import("./pages/EditFinancialPage"));

// Componente de loading para tela completa (fora do MainLayout)
const FullScreenLoadingFallback = React.memo(() => (
	<Box
		sx={{
			position: "fixed",
			top: 0,
			left: 0,
			width: "100vw",
			height: "100vh",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			flexDirection: "column",
			gap: 2,
			backgroundColor: "background.body",
			zIndex: 9999,
		}}
	>
		<CircularProgress size="lg" />
		<Box sx={{ fontSize: "sm", color: "text.secondary" }}>Carregando...</Box>
	</Box>
));

// Componente de loading para área de conteúdo (dentro do MainLayout)
const ContentLoadingFallback = React.memo(() => (
	<Box
		sx={{
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			height: "100%",
			width: "100%",
			minHeight: "80vh",
			flexDirection: "column",
			gap: 2,
		}}
	>
		<CircularProgress size="lg" />
		<Box sx={{ fontSize: "sm", color: "text.secondary" }}>Carregando...</Box>
	</Box>
));

/**
 * Componente para proteger rotas que exigem autenticação
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
	if (!isAuthenticated()) {
		return <Navigate to="/login" replace />;
	}

	return <>{children}</>;
};

/**
 * Layout principal da aplicação com Sidebar e conteúdo
 */
function MainLayout({ children }: { children: React.ReactNode }) {
	const navigate = useNavigate();
	const location = useLocation();
	const currentPage = location.pathname.substring(1) || "home";

	// Responsividade igual à Sidebar
	const [isCustomMobile, setIsCustomMobile] = React.useState(false);
	React.useEffect(() => {
		const handleResize = () => {
			const width = window.innerWidth;
			const height = window.innerHeight;
			const aspectRatio = width / height;
			const isMobile =
				width < 1200 ||
				aspectRatio < 1.3 ||
				height < 600 ||
				(width < 1400 && height < 800);
			setIsCustomMobile(isMobile);
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return (
		<Box sx={{ display: "flex", minHeight: "100dvh" }}>
			<Sidebar
				onNavigate={(page: string) => navigate(`/${page}`)}
				currentPage={currentPage}
			/>
			<Box
				sx={{
					flex: 1,
					width: "100%",
					minWidth: 0,
					// Em desktop, deixa espaço para sidebar; em mobile, ocupa 100vw
					ml: isCustomMobile ? 0 : "0px",
					// Se quiser garantir que nunca fique sob a sidebar, pode usar paddingLeft igual à largura da sidebar em desktop
					// paddingLeft: isCustomMobile ? 0 : 'var(--Sidebar-width)',
					transition: "all 0.2s",
				}}
			>
				{children}
			</Box>
		</Box>
	);
}

/**
 * Componente principal da aplicação Ecobalance
 *
 * Funcionalidades:
 * - Gerenciamento de navegação entre páginas usando React Router
 * - Layout responsivo com barra lateral e conteúdo principal
 * - Autenticação de usuários
 * - Tema escuro/claro
 */
export default function EcoBalanceApp() {
	useEffect(() => {
		initializeCSRF();
	}, []);

	return (
		<CssVarsProvider
			theme={customTheme}
			defaultMode="system"
			disableTransitionOnChange
		>
			<CssBaseline />
			<NotificationProvider>
				<BrowserRouter
					future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
				>
					<Routes>
						<Route
							path="/login"
							element={
								<Suspense fallback={<FullScreenLoadingFallback />}>
									<LoginPage />
								</Suspense>
							}
						/>

						<Route
							path="/"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<HomeTemporariaPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/home"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<HomeTemporariaPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/clientes"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<ClientesPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/clientes/visualizar/:id"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<ClientesPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/clientes/editar/:id"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<ClientesPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/clientes/cadastro"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<ClientesPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/propriedades"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<PropriedadesPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/propriedades/visualizar/:id"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<PropriedadesPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/propriedades/editar/:id"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<PropriedadesPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/propriedades/cadastro"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<PropriedadesPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						{/* Sócios */}
						<Route
							path="/socios"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<SociosPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/socios/visualizar/:id"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<SociosPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/socios/editar/:id"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<SociosPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/socios/cadastro"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<SociosPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<RatificacoesPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/nova-ratificacao"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<NovaRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						{/* Rotas Financeiras */}
						<Route
							path="/financeiro"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<FinancialPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/financeiro/:id"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<FinancialDetailsPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/financeiro/:id/editar"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditFinancialPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/financeiro/novo"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<NewFinancialPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						{/* Rotas de Edição de Ratificação */}
						<Route
							path="/ratificacoes/:id"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/editar"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/propriedade"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/propriedade/editar"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/proprietarios"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/proprietarios/:clienteId"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/proprietarios/:clienteId/editar"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/proprietarios/novo"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/proprietarios/buscar"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/documentos"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/documentos/upload"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/revisao"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/configuracao"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/historico"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/ratificacoes/:id/status"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<EditarRatificacaoPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/novo-usuario"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<NovoUsuarioPage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/configuracoes"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											{isAdmin() ? (
												<ConfiguracoesPage />
											) : (
												<EmImplementacaoPage />
											)}
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/suporte"
							element={
								<ProtectedRoute>
									<MainLayout>
										<Suspense fallback={<ContentLoadingFallback />}>
											<SuportePage />
										</Suspense>
									</MainLayout>
								</ProtectedRoute>
							}
						/>

						<Route path="*" element={<Navigate to="/" replace />} />
					</Routes>

					{/* Sistema de Notificações Global */}
					<NotificationSnackbar />
				</BrowserRouter>
			</NotificationProvider>
		</CssVarsProvider>
	);
}
